import type { IPaymentService } from "@/services/interfaces/IPaymentService";
import type {
  EscrowRecord,
  TransactionResult,
  WalletBalance,
  Transaction,
  SupportedCurrency,
  KnownCurrency,
} from "@/lib/types";
import { KNOWN_CURRENCIES, isKnownCurrency } from "@/lib/types";
import type { PolygonClient } from "@/lib/polygon";
import { supabase } from "@/lib/supabase";
import { currencyFromAddress } from "@/config/tokens";
import { assertOnActiveChain } from "@/lib/chainGuard";

const ESCROW_STATUS_MAP: Record<number, EscrowRecord["status"]> = {
  0: "funded", // Active
  1: "funded", // Funded
  2: "partially_released",
  3: "fully_released",
  4: "disputed", // Frozen
};

export class PolygonPaymentService implements IPaymentService {
  constructor(private polygonClient: PolygonClient) {}

  async createEscrow(
    _questId: string,
    _amount: number,
    _currency: SupportedCurrency,
  ): Promise<EscrowRecord> {
    // Escrow creation happens during questManager.fundQuest() in SupabaseQuestService.
    // This is a no-op since the quest creation flow handles it.
    throw new Error("Escrow is created during quest funding, not separately.");
  }

  async releasePayment(
    _escrowId: string,
    _recipientAddress: string,
    _amount: number,
  ): Promise<TransactionResult> {
    // Payment release happens automatically during taskManager.approveSubmission()
    // via OPERATOR_ROLE cascade to EscrowVault.releasePayment.
    throw new Error("Payment is released during submission approval, not separately.");
  }

  async getEscrowStatus(questId: string): Promise<EscrowRecord> {
    // Look up chain_quest_id + currency from Supabase
    const { data: quest } = await supabase
      .from("quests")
      .select("chain_quest_id, currency, created_at, chain_id")
      .eq("id", questId)
      .single();

    if (!quest?.chain_quest_id) {
      return {
        id: questId,
        questId,
        totalAmount: 0,
        currency: isKnownCurrency(quest?.currency) ? quest.currency : "USDT",
        status: "funded",
        createdAt: quest?.created_at ?? new Date().toISOString(),
      };
    }

    // chain_quest_id is a per-chain counter -- reading it against the wrong
    // network returns a stranger's escrow. See lib/chainGuard.ts.
    assertOnActiveChain(quest.chain_id, "quest");
    const escrow = await this.polygonClient.getEscrow(BigInt(quest.chain_quest_id));
    // Resolve currency from on-chain token address, fallback to DB
    let currency: KnownCurrency;
    try {
      currency = currencyFromAddress(escrow.token);
    } catch {
      currency = isKnownCurrency(quest.currency) ? quest.currency : "USDT";
    }

    return {
      id: questId,
      questId,
      totalAmount: escrow.totalAmount,
      currency,
      status: ESCROW_STATUS_MAP[escrow.status] ?? "funded",
      createdAt: quest.created_at,
    };
  }

  async getBalance(walletAddress: string): Promise<WalletBalance> {
    const balances = await Promise.all(
      KNOWN_CURRENCIES.map(async (currency) => ({
        currency,
        amount: await this.polygonClient.getTokenBalance(currency, walletAddress),
      })),
    );
    return { address: walletAddress, balances };
  }

  async getTransactionHistory(_walletAddress: string): Promise<Transaction[]> {
    // Full implementation needs an indexer. Return empty for MVP.
    return [];
  }
}
